import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Sparkles, Flame, Leaf } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import CandleFlame from "@/components/CandleFlame";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    (async () => {
      const [pRes, cRes] = await Promise.all([api.get("/products", { params: { featured: true } }), api.get("/categories")]);
      setProducts(pRes.data);
      setCategories(cRes.data);
    })();
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden warm-bg">
        <div className="grain-overlay" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center py-16 sm:py-24 lg:py-28 relative">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))]/60 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Sparkles className="w-3 h-3 text-candle-terracotta" /> Handpoured in small batches
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[hsl(var(--foreground))]">
              Light Up Your <em className="not-italic text-candle-terracotta">Little Moments.</em>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-lg leading-relaxed fade-up fade-up-delay-1">
              Hand-poured candles made to bring warmth, comfort, and a little magic into your everyday.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 fade-up fade-up-delay-2">
              <Link to="/shop">
                <Button data-testid="hero-cta-shop-candles" size="lg" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))] px-7">
                  Shop Candles <ChevronRight className="ml-1 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/shop">
                <Button data-testid="hero-cta-explore-collections" size="lg" variant="outline" className="rounded-full border-[hsl(var(--border))] px-7">
                  Explore Collections
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 fade-up fade-up-delay-3">
              <Feature icon={<Leaf className="w-4 h-4" />} label="100% Soy Wax" />
              <Feature icon={<Flame className="w-4 h-4" />} label="50+ Hour Burn" />
              <Feature icon={<Sparkles className="w-4 h-4" />} label="Handcrafted with Love" />
            </div>
          </div>

          <div className="relative fade-up fade-up-delay-2">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(60,30,10,0.35)]">
              <img src="https://images.unsplash.com/photo-1603006905003-be475563bc59?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85"
                alt="Cozy candle interior" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-candle-espresso/40 via-transparent to-transparent" />
            </div>
            {/* Floating flame widget */}
            <div className="absolute -bottom-8 -left-8 hidden sm:block float-soft">
              <div className="bg-[hsl(var(--card))]/90 backdrop-blur-md rounded-2xl p-4 border border-[hsl(var(--border))] shadow-xl flex items-center gap-3">
                <CandleFlame size={90} />
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Now Burning</div>
                  <div className="font-serif text-lg">Sandalwood & Amber</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">Discover</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl">Find Your Perfect Glow</h2>
          </div>
          <Link to="/shop" className="hidden sm:inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            View all <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {categories.map((c) => (
            <Link key={c.id} to={`/shop?category=${c.id}`} data-testid={`category-card-${c.id}`}
              className="group relative aspect-[4/5] rounded-2xl overflow-hidden border border-[hsl(var(--border))] candle-card">
              <img src={c.image} alt={c.name} loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-candle-espresso/85 via-candle-espresso/25 to-transparent" />
              <div className="absolute bottom-0 p-4 sm:p-5 text-white">
                <div className="font-serif text-xl sm:text-2xl">{c.name}</div>
                <div className="text-xs sm:text-sm text-white/80 mt-0.5">{c.description}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="bg-[hsl(var(--muted))]/40 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">Bestsellers</div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl">Made to Melt Your Stress Away</h2>
            </div>
            <Link to="/shop" className="hidden sm:inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
              Shop all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {products.slice(0, 8).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div className="relative aspect-[5/4] rounded-3xl overflow-hidden order-2 lg:order-1">
          <img src="https://images.unsplash.com/photo-1635435605591-217d993deb88?w=1200&q=85"
            alt="Handpoured process" className="w-full h-full object-cover" />
        </div>
        <div className="order-1 lg:order-2">
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">Our Craft</div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">Slowly Poured in Small Batches.</h2>
          <p className="mt-5 text-muted-foreground leading-relaxed">
            Every candle is poured by hand in tiny batches from 100% natural soy wax, essential oils, and lead-free cotton wicks. Because things worth burning slowly are worth making slowly.
          </p>
          <div className="mt-6 grid grid-cols-3 gap-4 max-w-md">
            <Stat n="12+" l="Signature Scents" />
            <Stat n="50h" l="Clean Burn" />
            <Stat n="100%" l="Soy Wax" />
          </div>
          <Link to="/about"><Button variant="outline" className="rounded-full mt-6">Read our story</Button></Link>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
        <div className="p-10 sm:p-14 rounded-3xl bg-gradient-to-br from-candle-cream to-candle-beige border border-[hsl(var(--border))]">
          <div className="text-xs uppercase tracking-[0.3em] text-candle-brown mb-3">Members Only</div>
          <h3 className="font-serif text-3xl sm:text-4xl mb-3 text-candle-espresso">Receive 10% Off Your First Warm Batch</h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">Slow scent stories, seasonal drops, and quiet moments — straight to your inbox.</p>
          <form onSubmit={(e)=>e.preventDefault()} className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
            <input type="email" placeholder="you@cozyhome.com" className="flex-1 px-4 py-3 rounded-full border border-[hsl(var(--border))] bg-white text-sm" />
            <Button className="rounded-full bg-candle-espresso text-white hover:bg-candle-espresso/90">Get my glow</Button>
          </form>
          <div className="mt-4 text-xs text-muted-foreground">Code auto-applies at checkout · {formatINR(0)} shipping over ₹999</div>
        </div>
      </section>
    </div>
  );
}

const Feature = ({ icon, label }) => (
  <div className="flex items-center gap-2 text-sm text-muted-foreground">
    <span className="w-8 h-8 rounded-full bg-[hsl(var(--card))] border border-[hsl(var(--border))] flex items-center justify-center text-candle-terracotta">{icon}</span>
    {label}
  </div>
);
const Stat = ({ n, l }) => (
  <div>
    <div className="font-serif text-2xl sm:text-3xl text-candle-espresso">{n}</div>
    <div className="text-xs text-muted-foreground">{l}</div>
  </div>
);
